import { currentTargetRequired, mediaLoader, qsaOptional, qsOptional, qsRequired } from '@/scripts/core/global'
import { type SlideChangedEvent } from '@/scripts/types/events'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export function isSliderComponent(obj: HTMLElement): obj is SliderComponent {
	if (!obj) return false
	if (obj.localName !== 'slider-component') return false
	return true
}

export class SliderComponent extends UcoastEl {
	static htmlSelector = 'slider-component'
	slider: HTMLElement
	sliderItems?: NodeListOf<HTMLElement>
	sliderItemsToShow?: HTMLElement[]
	sliderItemOffset?: number
	slidesPerPage?: number
	totalPages?: number
	currentPage?: number = 1
	slideScrollPosition?: number
	enableSliderLooping: boolean
	currentPageElement?: HTMLElement
	pageTotalElement?: HTMLElement
	prevButton?: HTMLButtonElement
	nextButton?: HTMLButtonElement
	resizeObserver: ResizeObserver
	private stopMouseDrag?: () => void
	constructor() {
		super()
		this.slider = qsRequired('[id^="Slider-"]', this)
		this.sliderItems = qsaOptional('[id^="Slide-"]', this)
		this.enableSliderLooping = false
		this.currentPageElement = qsOptional('.slider-counter--current', this)
		this.pageTotalElement = qsOptional('.slider-counter--total', this)
		this.prevButton = qsOptional('button[name="previous"]', this)
		this.nextButton = qsOptional('button[name="next"]', this)

		this.initPages()
		this.resizeObserver = new ResizeObserver((_entries: ResizeObserverEntry[]) =>
			this.initPages()
		)

		this.slider.addEventListener('scroll', this.update.bind(this))
		this.slider.addEventListener('keydown', this.onSliderKeyDown.bind(this))
		this.prevButton?.addEventListener('click', this.onButtonClick.bind(this))
		this.nextButton?.addEventListener('click', this.onButtonClick.bind(this))
		this.initDraggableSlider()
	}

	override connectedCallback() {
		super.connectedCallback()
		this.resizeObserver.observe(this.slider)
		this.initPages()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.resizeObserver.disconnect()
		this.stopMouseDrag?.()
	}

	initPages() {
		this.sliderItemsToShow = this.sliderItems ? Array.from(this.sliderItems).filter(
			(element) => element.clientWidth > 0
		) : []
		this.sliderItemOffset = this.sliderItemsToShow.length > 1
			? Math.abs(this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft)
			: 0
		this.slidesPerPage = this.sliderItemOffset
			? Math.max(1, Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset))
			: 1
		this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1)
		this.update()
	}

	resetPages() {
		this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
		this.initPages()
	}

	createSlideChangedEvent(): SlideChangedEvent {
		const currentPage = this.currentPage
		if (!currentPage)
			throw new Error('cannot createSlideChangedEvent - currentPage is undefined')
		if (!this.sliderItemsToShow)
			throw new Error('cannot createSlideChangedEvent - sliderItemsToShow is undefined')
		const currentElement = this.sliderItemsToShow[currentPage - 1]
		if (!currentElement)
			throw new Error('cannot createSlideChangedEvent - currentElement is undefined')
		return new CustomEvent('slideChanged', {
			detail: {
				currentPage,
				currentElement,
			},
		})
	}

	update() {
		if (!this.slider) return

		const previousPage = this.currentPage
		if (!this.sliderItemOffset || this.sliderItemOffset === 0) {
			this.currentPage = 1
		} else {
			this.currentPage = Math.max(1, Math.min(this.totalPages || 1, Math.round(Math.abs(this.slider.scrollLeft) / this.sliderItemOffset) + 1))
		}

		if (this.currentPageElement && this.pageTotalElement) {
			this.currentPageElement.textContent = `${this.currentPage}`
			this.pageTotalElement.textContent = `${this.totalPages}`
		}

		if (this.currentPage != previousPage && this.sliderItemsToShow?.length) {
			this.dispatchEvent(this.createSlideChangedEvent())
			mediaLoader(true)
		}

		if (this.enableSliderLooping) return
		if (!this.sliderItemsToShow?.length) {
			this.prevButton?.setAttribute('disabled', 'disabled')
			this.nextButton?.setAttribute('disabled', 'disabled')
			return
		}

		if (
			this.prevButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length &&
			this.isSlideVisible(this.sliderItemsToShow[0]) &&
			this.slider.scrollLeft === 0
		) {
			this.prevButton.setAttribute('disabled', 'disabled')
		} else if (this.prevButton) {
			this.prevButton.removeAttribute('disabled')
		}

		if (
			this.nextButton &&
			this.sliderItemsToShow &&
			this.sliderItemsToShow.length > 0 &&
			this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
		) {
			this.nextButton.setAttribute('disabled', 'disabled')
		} else if (this.nextButton) {
			this.nextButton.removeAttribute('disabled')
		}
	}

	onSliderKeyDown(event: KeyboardEvent) {
		if (event.target !== this.slider || !this.slider.hasAttribute('tabindex')) return
		const step = this.sliderItemOffset || this.slider.clientWidth
		let position: number
		switch (event.key) {
			case 'ArrowLeft': position = this.slider.scrollLeft - step; break
			case 'ArrowRight': position = this.slider.scrollLeft + step; break
			case 'Home': position = 0; break
			case 'End': position = this.slider.scrollWidth; break
			default: return
		}
		event.preventDefault()
		this.setSlidePosition(position)
	}

	isSlideVisible(element: HTMLElement, offset = 0) {
		const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset
		return (
			element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
			element.offsetLeft >= this.slider.scrollLeft
		)
	}

	onButtonClick(event: MouseEvent) {
		event.preventDefault()
		const currentTarget = currentTargetRequired(event)
		if (!(currentTarget instanceof HTMLButtonElement)) return
		if (!this.sliderItemOffset) return
		const step = currentTarget.dataset.step ? parseInt(currentTarget.dataset.step) : 1
		this.slideScrollPosition =
			currentTarget.name === 'next'
				? this.slider.scrollLeft + step * this.sliderItemOffset
				: this.slider.scrollLeft - step * this.sliderItemOffset
		this.setSlidePosition(this.slideScrollPosition)
	}

	setSlidePosition(position: number) {
		this.slider.scrollTo({
			left: position,
		})
	}

	initDraggableSlider() {
		this.slider.addEventListener('mousedown', this.onMouseDown.bind(this))
	}

	onMouseDown(event: MouseEvent) {
		this.stopMouseDrag?.()
		if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
		if (this.slider.scrollWidth <= this.slider.clientWidth) return
		const target = event.target
		if (!(target instanceof Element) || target.closest(
			'button, input, select, textarea, label, summary, video, audio, [role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="slider"], [contenteditable]:not([contenteditable="false"]), [data-no-slider-drag]'
		)) return

		const clientX = event.clientX
		const clientY = event.clientY
		const initialScroll = this.slider.scrollLeft
		let dragging = false
		let clickTimeout: number | undefined

		const onMouseMove = (e: MouseEvent) => {
			if ((e.buttons & 1) === 0) {
				cleanup()
				return
			}
			const deltaX = clientX - e.clientX
			if (!dragging) {
				const deltaY = Math.abs(clientY - e.clientY)
				if (deltaY >= 8 && deltaY > Math.abs(deltaX)) {
					cleanup()
					return
				}
				if (Math.abs(deltaX) < 8) return
				dragging = true
			}
			e.preventDefault()
			this.slider.scrollLeft = initialScroll + deltaX
		}

		const suppressClick = (e: MouseEvent) => {
			if (!dragging || e.detail === 0 || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return
			e.preventDefault()
			e.stopImmediatePropagation()
			cleanup()
		}
		const preventNativeDrag = (e: DragEvent) => e.preventDefault()
		const detachTracking = () => {
			document.removeEventListener('mousemove', onMouseMove, true)
			document.removeEventListener('mouseup', onMouseUp, true)
			window.removeEventListener('blur', cleanup)
			this.slider.removeEventListener('dragstart', preventNativeDrag)
		}
		const cleanup = () => {
			detachTracking()
			this.slider.removeEventListener('click', suppressClick, true)
			window.clearTimeout(clickTimeout)
			this.stopMouseDrag = undefined
		}
		const onMouseUp = () => {
			detachTracking()
			// The native click follows mouseup synchronously; do not consume a later interaction.
			if (dragging) clickTimeout = window.setTimeout(cleanup, 0)
			else cleanup()
		}

		this.stopMouseDrag = cleanup
		document.addEventListener('mousemove', onMouseMove, true)
		document.addEventListener('mouseup', onMouseUp, true)
		window.addEventListener('blur', cleanup)
		this.slider.addEventListener('dragstart', preventNativeDrag)
		this.slider.addEventListener('click', suppressClick, true)
	}
}
